import type { RouterContextType } from "@/api/permissions/permissions.api";
import { authGuardWithPermissions } from "@/utils/authGuard";
import Header from "@/components/ui/Header/Header";
import { Box, Stack } from "@mantine/core";
import { createFileRoute, retainSearchParams, Outlet, useNavigate } from "@tanstack/react-router";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import styles from "./assignServiceProviderLayout.module.scss";
import AssignServiceProviderList from "@/components/features/AssignServiceProviderList/AssignServiceProviderList";
import { APIProvider } from "@vis.gl/react-google-maps";
import { getGoogleMapsApiKey, REQUIRED_GOOGLE_MAPS_LIBRARIES } from "@/lib/GoogleMaps/config";
import { useImpersonationStore } from "@/store/useImpersonationStore";

const BANNER_HEIGHT = 66.1953;

export const Route = createFileRoute(
  "/work-orders/$workOrderId/$visitId/assign-service-provider/_assignServiceProviderLayout",
)({
  component: AssignServiceProviderLayout,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function AssignServiceProviderLayout() {
  const { workOrderId, visitId } = Route.useParams();
  const navigate = useNavigate();
  const apiKey = getGoogleMapsApiKey();
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);

  const handleClose = () => {
    navigate({
      to: "/work-orders/$workOrderId/$visitId",
      params: { workOrderId, visitId },
    });
  };

  return (
    <APIProvider apiKey={apiKey} libraries={REQUIRED_GOOGLE_MAPS_LIBRARIES as any}>
      <Box style={{ background: "var(--bg-surface-base, #E2E6EB)" }}>
        <Stack gap={0} style={{ height: "100%" }}>
          <Box
            style={{
              position: "sticky",
              top: isImpersonating ? BANNER_HEIGHT : 0,
              zIndex: 20,
            }}
          >
            <Header title={"Assign Service Provider"} onClose={() => handleClose()} />
          </Box>
          <Box flex={1} className={styles.stage}>
            <AssignServiceProviderList>
              <Outlet />
            </AssignServiceProviderList>
          </Box>
          {/* 11.R: "Back to Visit Details" removed. It called the same
              handleClose as the X in the page header, so the screen offered two
              identical exits. */}
        </Stack>
      </Box>
    </APIProvider>
  );
}
